import type { ChatSession } from '../types/domain';
import type { HandlerType, HandlerResultMap } from '../types/handlerResults';

/**
 * Type-safe chat handler interface
 * Handlers process specialized messages within chat sessions
 */
export interface ChatHandler<THandlerType extends HandlerType> {
  readonly handlerType: THandlerType;
  
  canHandle: (session: ChatSession) => boolean;
  
  handleMessage: (
    content: string,
    session: ChatSessionWithHandler<THandlerType>, 
    context?: any
  ) => Promise<{
    response: string;
    handlerResults?: HandlerResultMap[THandlerType];
  }>;
  
  shouldTransitionToGeneral: (
    result: HandlerResultMap[THandlerType]
  ) => boolean;
}

export type ChatSessionWithHandler<THandlerType extends HandlerType> = ChatSession & {
  handlerType: THandlerType;
  handlerResults: Record<string, any>;
};

export class ChatHandlerRegistry {
  private handlers = new Map<HandlerType, ChatHandler<any>>();

  register<THandlerType extends HandlerType>(handler: ChatHandler<THandlerType>): void {
    if (this.handlers.has(handler.handlerType)) {
      throw new Error(`Chat handler for type '${handler.handlerType}' already registered`);
    }
    
    this.validateHandler(handler);
    this.handlers.set(handler.handlerType, handler);
  }

  getHandler<THandlerType extends HandlerType>(
    handlerType: THandlerType
  ): ChatHandler<THandlerType> | undefined {
    return this.handlers.get(handlerType) as ChatHandler<THandlerType> | undefined;
  }

  private validateHandler<THandlerType extends HandlerType>(
    handler: ChatHandler<THandlerType>
  ): void {
    if (!handler.handlerType) {
      throw new Error('Handler must have a handlerType');
    }
    
    if (typeof handler.canHandle !== 'function') {
      throw new Error('Handler must implement canHandle method');
    }
    
    if (typeof handler.handleMessage !== 'function') {
      throw new Error('Handler must implement handleMessage method');
    }
    
    if (typeof handler.shouldTransitionToGeneral !== 'function') {
      throw new Error('Handler must implement shouldTransitionToGeneral method');
    }
  }
}

// Export singleton instance for global use
export const chatHandlerRegistry = new ChatHandlerRegistry();